import { theme } from '@prophecy/theme';
import {
  Stack,
  Tabs,
  Resizable,
  StackItem,
  Input,
  SwitchTabs,
  OverlaySpinner,
  DataNode,
  Centered
} from '@prophecy/ui-v3';
import { addLeadingSlash } from '@prophecy/ui-v3/Editor/utils';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useGlobalVariables } from '../../../../../context/globalConfig/securedConfigHook';
import { ProjectBrowserRootCls } from '../../../../../layout/config';
import { StyledTree } from '../../../../Sql/ProjectBrowser/styled';
import { FileFormats } from '../../../config';
import { useImportState } from '../../useImportState';
import { getJobFolderType } from '../getJobFolderType';
import { TreeItem } from '../types';
import { getTreeDataWithExtension, searchAndFilterWithExtension } from '../utils';
import { FolderEntityNode } from './FolderEntityNode';

const StyledStackItem = styled(StackItem)`
  overflow: auto;
  min-height: 0;
  max-height: 100%;
`;

const StyledTreeView = styled(StyledTree)`
  height: 100%;
`;

const StyledProjectBrowserStack = styled(Stack)`
  border-left: 1px solid ${theme.colors.gray200};
`;

export const ProjectBrowser = () => {
  const {
    browserDataLoading,
    projectBrowserState: {
      projectBrowserWidth,
      _setProjectBrowserWidth,
      treeData: filteredTreeData,
      setTreeData: setFilteredTreeData
    },
    formDataWithActions: {
      formContext: { getValues }
    },
    transpilerState: {
      uploadedFilePaths,
      transpilerEntityTypes: { jobs, pipelines }
    }
  } = useImportState();

  const source = getValues('source') as FileFormats;

  const activeKeys = useMemo(() => {
    const selectedKeys: string[] = [];
    jobs?.forEach((job) => {
      let path = job.filePath;
      const filePath = addLeadingSlash(path);
      selectedKeys.push(filePath);
    });
    pipelines.forEach((pipeline) => {
      selectedKeys.push(pipeline.filePath);
      const psets = pipeline.transpilerOptions?.targetPsets || [];
      psets.forEach((pset) => selectedKeys.push(pset));
      const pathToReplaced = pipeline.transpilerOptions?.pathsToBeReplaced || [];
      pathToReplaced.forEach((path) => selectedKeys.push(path));
      pipeline.configurations?.forEach((config) => {
        const psetss = config?.configParams?.psets || [];
        psetss.forEach((pset) => selectedKeys.push(pset));
      });
    });
    return selectedKeys;
  }, [jobs, pipelines]);

  const [searchStr, setSearchStr] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const isJobEnabled = useGlobalVariables().isTranspilerJobEnabled;

  const activeExtensions = getJobFolderType(source, !!isJobEnabled);
  const isPending = activeTab === 'pending';

  const { selectedKeys, filteredPaths } = useMemo(
    () =>
      searchAndFilterWithExtension(
        uploadedFilePaths,
        searchStr,
        isPending ? activeExtensions : undefined,
        isPending ? activeKeys : undefined
      ),
    [uploadedFilePaths, activeKeys, activeExtensions, searchStr, isPending]
  );

  useEffect(() => {
    const filteredTreeData = getTreeDataWithExtension(filteredPaths, source, !!isJobEnabled);
    setFilteredTreeData(filteredTreeData);
  }, [filteredPaths, source, isJobEnabled, setFilteredTreeData]);

  const [expandedKeys, setExpandedKeys] = useState(activeKeys);

  useEffect(() => {
    if (expandedKeys.length < 0 && selectedKeys.length > 0) {
      setExpandedKeys(selectedKeys);
    }
  }, [selectedKeys, expandedKeys]);

  const onExpand = (
    _: unknown,
    info: {
      node: DataNode;
      expanded: boolean;
    }
  ) => {
    const currentKey = info.node.key as string;
    setExpandedKeys(
      info.expanded ? [...expandedKeys, currentKey] : expandedKeys.filter((key) => !key.startsWith(currentKey))
    );
  };

  const tabViewUI = (
    <Stack height='100%' width='100%'>
      {browserDataLoading ? (
        <OverlaySpinner spinning={true} />
      ) : filteredTreeData.length > 0 ? (
        <StyledTreeView
          treeData={filteredTreeData}
          selectable
          selectedKeys={selectedKeys}
          autoExpandParent={true}
          virtual={true}
          onExpand={onExpand}
          expandedKeys={expandedKeys}
          titleRender={(node) => <FolderEntityNode node={node as TreeItem} />}
        />
      ) : (
        <Centered>No File Data</Centered>
      )}
    </Stack>
  );

  const onChangeSearch = (value: string) => {
    setSearchStr(value);
    if (value === '') {
      setExpandedKeys(selectedKeys);
    } else {
      setExpandedKeys(uploadedFilePaths);
    }
  };

  return (
    <Resizable
      className={ProjectBrowserRootCls}
      direction='left'
      maxWidth='500px'
      minWidth='310px'
      handleStyles={{
        zIndex: theme.zLayer.s
      }}
      size={{ width: projectBrowserWidth, height: '100%' }}
      onResizeStop={(e, direction, ref, d) => {
        _setProjectBrowserWidth(projectBrowserWidth + d.width);
      }}>
      <StyledProjectBrowserStack height='100%' width='100%' gap={theme.spaces.x12} padding={`0 ${theme.spaces.x6} 0 0`}>
        <StackItem shrink='0'>
          <Input
            value={searchStr}
            inputSize='s'
            onChange={onChangeSearch}
            placeholder='Search'
            data-test-id='transpiler-project-browser-search-input'
          />
        </StackItem>
        <StyledStackItem grow='1'>
          <SwitchTabs activeKey={activeTab} size='s' onChange={setActiveTab}>
            <Tabs.TabPane tab='All files' key='all'>
              {tabViewUI}
            </Tabs.TabPane>
            <Tabs.TabPane tab='Pending' key='pending'>
              {tabViewUI}
            </Tabs.TabPane>
          </SwitchTabs>
        </StyledStackItem>
      </StyledProjectBrowserStack>
    </Resizable>
  );
};
